import { MultipleAutocomplete } from '@src/common/components/Autocomplete';
import { Checkbox } from '@src/common/components/Checkbox';
import { TextInput } from '@src/common/components/TextInput';
import { css } from '@src/common/styles/Styles';
import type { ReactNode } from 'react';
import { useId } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Props<T extends FieldValues> {
  control: Control;
  fieldName: Path<T>;
  options: { value: string; label: string }[];
  placeholder?: string;
  alwaysShrinkLabel?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  renderTags?: (value: unknown[]) => ReactNode;
}

export const FormMultipleOptions = <T extends FieldValues>({
  control,
  fieldName,
  options,
  placeholder,
  alwaysShrinkLabel,
  readOnly,
  renderTags,
  disabled,
}: Props<T>) => {
  const fieldId = useId();
  const { t } = useTranslation();

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <MultipleAutocomplete
          {...field}
          readOnly={readOnly}
          disabled={disabled}
          id={fieldId}
          options={options}
          value={
            field.value
              ? options.filter((option) => field.value.includes(option.value))
              : []
          }
          onChange={(_, data) =>
            field.onChange(
              data.map(
                (option) => (option as { value: string; label: string }).value,
              ),
            )
          }
          disableCloseOnSelect
          getOptionLabel={(option) =>
            (option as { value: string; label: string }).label
          }
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            return (
              <li key={key} {...optionProps}>
                <Checkbox
                  style={{ marginRight: 8 }}
                  checked={selected}
                  label={(option as { value: string; label: string }).label}
                />
              </li>
            );
          }}
          renderTags={renderTags}
          renderInput={(params) => (
            <TextInput
              {...params}
              alwaysShrinkLabel={alwaysShrinkLabel}
              placeholder={field.value?.length > 0 ? undefined : placeholder}
              className={fullWidth}
              label={t(`fields.${fieldName}`)}
              error={error && t(`formErrors.${error.message}`)}
            />
          )}
        />
      )}
    />
  );
};

const fullWidth = css({
  flex: 1,
});
